<template>
  <div
    class="patients-mgmt-page"
    :class="loadingContent ? 'loading large' : ''"
  >
    <!-- Resumo de licenças (igual ao dashboard: badge muda de cor conforme
         a disponibilidade). Só aparece quando há licenças contratadas. -->
    <div
      v-if="hasLicenses"
      class="pmg-license-summary"
    >
      Licenças disponíveis:
      <lila-license-badge :licenses="licenses" />
      <span class="pmg-license-dot">•</span>
      Precisa de mais licenças?
      <a
        class="pmg-license-contact"
        href="mailto:contato@lilasaude.com.br"
      >Entre em contato!</a>
    </div>

    <lila-message :alert-data="alert" />

    <!-- Filtros -->
    <div
      v-show="!loadingContent"
      class="pmg-filters"
    >
      <div class="pmg-filter pmg-filter--search">
        <label class="pmg-filter-label">
          Nome ou Nº de Matrícula do Paciente
        </label>
        <form @submit.prevent="applySearch">
          <lila-input
            v-model="filters.search"
            type="inputSearch"
            class="with-shadow"
            placeholder="Pesquisar"
            :btn-clear-search="filters.searchActive"
            @custom-clear-search-input-event="clearSearch"
          />
        </form>
      </div>

      <div class="pmg-filter">
        <label class="pmg-filter-label">Equipe</label>
        <div class="pmg-filter-picker-wrap">
          <button
            type="button"
            class="pmg-filter-select"
            :class="{ 'has-clear': filters.team }"
            @click.stop="filterPickerOpen = !filterPickerOpen"
          >
            <span :class="{ 'is-placeholder': !filters.team }">
              {{ filters.team || "Selecione" }}
            </span>
            <span
              v-if="!filters.team"
              class="pmg-filter-caret"
            />
          </button>
          <button
            v-if="filters.team"
            type="button"
            class="pmg-filter-clear"
            aria-label="Limpar filtro de equipe"
            @click.stop="applyTeamFilter(null)"
          >
            &times;
          </button>
          <div
            v-if="filterPickerOpen"
            class="pmg-filter-picker"
          >
            <team-tree-picker
              :tree="teamTree"
              :immediate="true"
              :units-selectable="false"
              :current-id="filters.teamId"
              :show-current="false"
              all-label="Todas as equipes"
              title="Filtrar por equipe"
              @select="applyTeamFilter"
              @cancel="filterPickerOpen = false"
            />
          </div>
        </div>
      </div>

      <span class="pmg-divider" />

      <div class="pmg-filter pmg-filter--add">
        <label class="pmg-filter-label">Adicionar Paciente</label>
        <button
          type="button"
          class="pmg-add-btn"
          @click="addPatient"
        >
          <svg
            class="pmg-add-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
            />
            <line
              x1="12"
              y1="8"
              x2="12"
              y2="16"
            />
            <line
              x1="8"
              y1="12"
              x2="16"
              y2="12"
            />
          </svg>
          Adicionar
        </button>
      </div>
    </div>

    <!-- Barra de ação em lote (bulk-edit) -->
    <div
      v-if="!loadingContent && selectedIds.length > 0"
      class="pmg-bulk-bar"
    >
      <span class="pmg-bulk-count">
        {{ selectedIds.length }}
        {{
          selectedIds.length === 1
            ? "paciente selecionado"
            : "pacientes selecionados"
        }}
      </span>

      <div class="pmg-bulk-action">
        <div class="pmg-bulk-picker-wrap">
          <button
            type="button"
            class="pmg-bulk-apply"
            :disabled="bulkSaving"
            @click.stop="bulkPickerOpen = !bulkPickerOpen"
          >
            {{ bulkSaving ? "Alterando…" : "Alterar equipe" }}
          </button>
          <div
            v-if="bulkPickerOpen"
            class="pmg-bulk-picker"
          >
            <team-tree-picker
              :tree="destinationTree"
              :units-selectable="false"
              title="Definir a equipe dos selecionados"
              :saving="bulkSaving"
              @select="applyBulkTeam($event)"
              @cancel="bulkPickerOpen = false"
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        class="pmg-bulk-clear"
        @click="clearSelection"
      >
        Limpar seleção
      </button>
    </div>

    <!-- Tabela -->
    <div
      v-show="!loadingContent"
      class="pmg-table-wrapper custom-scroll"
    >
      <table class="pmg-table">
        <thead>
          <tr>
            <th class="has-text-centered pmg-check-col">
              <label class="pmg-check">
                <input
                  type="checkbox"
                  :checked="allPageSelected"
                  :indeterminate.prop="
                    somePageSelected && !allPageSelected
                  "
                  @change="toggleSelectAllPage($event)"
                >
              </label>
            </th>
            <th>Nome do Paciente</th>
            <th>Sexo</th>
            <th>Idade</th>
            <th>CID</th>
            <th>Equipe</th>
            <th
              v-if="hasLicenses"
              class="has-text-centered"
            >
              Licenciado
            </th>
            <th class="has-text-centered">
              Editar
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            :class="{ 'is-selected': isSelected(row.id) }"
          >
            <td class="has-text-centered pmg-check-col">
              <label class="pmg-check">
                <input
                  type="checkbox"
                  :checked="isSelected(row.id)"
                  @change="toggleSelect(row)"
                >
              </label>
            </td>
            <td class="pmg-name">
              {{ row.name }}
            </td>
            <td>{{ row.gender || '—' }}</td>
            <td>{{ row.age }}</td>
            <td>{{ row.cid || '—' }}</td>
            <td>
              <div class="team-cell">
                <span
                  class="clickable-cell"
                  @click="aoClicarNaEquipe(row, $event)"
                >
                  {{ row.team || '—' }}
                </span>

                <teleport
                  v-if="selectedTeamRowId === row.id"
                  to="body"
                >
                  <div
                    class="pmg-team-picker"
                    :style="teamPickerStyle"
                  >
                    <team-tree-picker
                      :tree="destinationTree"
                      :units-selectable="false"
                      :current-id="row.teamId"
                      :title="`Trocar equipe · ${row.name}`"
                      :saving="row.updatingTeam"
                      @select="changePatientTeam(row, $event)"
                      @cancel="selectedTeamRowId = null"
                    />
                  </div>
                </teleport>
              </div>
            </td>
            <td
              v-if="hasLicenses"
              class="has-text-centered"
            >
              <label class="pmg-switch">
                <input
                  type="checkbox"
                  :checked="row.isLicensed"
                  :disabled="row.updatingLicense"
                  @change="toggleLicense(row, $event)"
                >
                <span class="pmg-slider" />
              </label>
            </td>
            <td class="has-text-centered">
              <button
                type="button"
                class="pmg-edit-btn"
                title="Editar paciente"
                @click.stop="editRow(row)"
              >
                <lila-svg-icon :src="require('@assets/icons/icon_editar.svg')" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        v-if="!loadingContent && filteredRows.length === 0"
        class="pmg-empty has-text-centered"
      >
        Nenhum paciente encontrado.
      </p>
    </div>

    <lila-pagination
      v-show="!loadingContent && filteredRows.length > 0"
      :pagination-config="pagination"
      @custom-change-page-event="changePage"
    />
  </div>
</template>

<script>
import Input from "@components/shared/fields/Input.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";
import LicenseBadge from "@components/shared/lila/LicenseBadge.vue";

export default {
  components: {
    "lila-input": Input,
    "lila-message": Message,
    "lila-pagination": Pagination,
    "lila-svg-icon": SvgIcon,
    "team-tree-picker": TeamTreePicker,
    "lila-license-badge": LicenseBadge
  },
  emits: ["custom-edit-event"],
  data() {
    return {
      loadingContent: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      patients: [],
      teams: [],
      // Equipes visíveis ao profissional (api/team/available), usadas
      // no filtro por equipe (já escopadas por role/subárvore).
      filterTargets: [],
      transferTargets: [],
      // Id da linha cujo dropdown de equipe está aberto (null = nenhum).
      selectedTeamRowId: null,
      teamPickerStyle: {},
      // Estado do dropdown de filtro por equipe.
      filterPickerOpen: false,
      // Bulk-edit: ids dos pacientes selecionados + estado da ação em lote.
      selectedIds: [],
      bulkPickerOpen: false,
      bulkSaving: false,
      filters: {
        search: "",
        searchActive: false,
        team: null,
        teamId: null
      },
      pagination: {
        current: 1,
        totalPages: 0,
        perPage: 10
      },
      // Licenças da instituição (resumo + coluna "Licenciado").
      licenses: {
        available: 0,
        total: 0,
        // Instituição sem teto de licenças (GET /api/Licenses → { unlimited: true }).
        unlimited: false
      }
    };
  },
  computed: {
    filteredRows() {
      const term = this.filters.search.trim().toLowerCase();

      return this.patients.filter((row) => {
        const matchesSearch =
          !term ||
          row.name.toLowerCase().includes(term) ||
          (row.registerNumber || "").toLowerCase().includes(term);
        const matchesTeam =
          !this.filters.team || row.team === this.filters.team;
        return matchesSearch && matchesTeam;
      });
    },
    pagedRows() {
      const start = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredRows.slice(start, start + this.pagination.perPage);
    },
    // Seleção (bulk) relativa à página visível.
    allPageSelected() {
      return (
        this.pagedRows.length > 0 &&
        this.pagedRows.every((row) => this.selectedIds.includes(row.id))
      );
    },
    somePageSelected() {
      return this.pagedRows.some((row) => this.selectedIds.includes(row.id));
    },
    // Só mostra o resumo/coluna de licença quando há licenças contratadas.
    hasLicenses() {
      return this.licenses.total > 0;
    },

    // Árvore Unidades › Equipes para o filtro, montada a partir das
    // equipes visíveis ao profissional (GET api/team/available, já escopado).
    // Cada equipe é uma folha que carrega o Path (unidades ancestrais).
    // Sem Path, todas caem na raiz → lista simples;
    // com Path, os grupos por unidade aparecem sozinhos.
    teamTree() {
      return this.buildTeamTree(this.filterTargets);
    },
    destinationTree() {
      return this.buildTeamTree(this.transferTargets);
    }
  },
  watch: {
    filteredRows() {
      this.recalculatePagination();
    }
  },
  async created() {
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("scroll", this.closeTeamPickerOnScroll, true);

    // Carrega as equipes antes dos pacientes para resolver o nome da equipe.
    await this.getTeams();
    this.getPatients();
    this.getLicenses();
    this.getFilterTargets();
    this.getTransferTargets();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("scroll", this.closeTeamPickerOnScroll, true);
  },
  methods: {
    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },

    async getTeams() {
      try {
        const res = await this.$axios.get("api/team", {
          headers: this.authHeader()
        });
        this.teams = (res.data || []).map((item) => ({
          id: item.Id,
          name: item.Name
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes:", error);
      }
    },

    toTargets(data) {
      const list = Array.isArray(data) ? data : (data && data.Data) || [];
      return list.filter(Boolean).map((item) => ({
        id: this.$Utilities.getValueIgnoreCase(item, "Id"),
        name: this.$Utilities.getValueIgnoreCase(item, "Name"),
        isTeam: Boolean(this.$Utilities.getValueIgnoreCase(item, "IsTeam")),
        path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map(
          (unit) => ({
            id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
            name: this.$Utilities.getValueIgnoreCase(unit, "Name")
          })
        )
      }));
    },

    async getFilterTargets() {
      try {
        const res = await this.$axios.get("api/team/available", {
          headers: this.authHeader()
        });
        this.filterTargets = this.toTargets(res.data).filter((target) => target.isTeam);
      } catch (error) {
        console.error("Erro ao carregar equipes do filtro:", error);
      }
    },

    // Equipes de destino válidas para a transferência (todas as equipes da
    // instituição para UnitAdmin e InstitutionAdmin), com o Path das unidades.
    async getTransferTargets() {
      try {
        const res = await this.$axios.get("api/team/transfer-targets", {
          headers: this.authHeader()
        });
        this.transferTargets = this.toTargets(res.data);
      } catch (error) {
        console.error("Erro ao carregar equipes de destino:", error);
      }
    },

    buildTeamTree(targets) {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };

      targets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    },

    // ---- Licenças -----------------------------------------------------
    async getLicenses() {
      try {
        const res = await this.$axios.get("api/Licenses", {
          headers: this.authHeader()
        });
        const data = res.data || {};
        this.licenses.unlimited = Boolean(
          this.$Utilities.getValueIgnoreCase(data, "Unlimited")
        );
        if (this.licenses.unlimited) {
          // Sem teto de licenças: a resposta omite os números — não tratar como zero.
          this.licenses.available = Infinity;
          this.licenses.total = Infinity;
          return;
        }
        this.licenses.available = Number(
          this.$Utilities.getValueIgnoreCase(data, "AvailableLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Available") ??
            0
        );
        this.licenses.total = Number(
          this.$Utilities.getValueIgnoreCase(data, "TotalLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Total") ??
            0
        );
      } catch (error) {
        console.error("Erro ao carregar licenças:", error);
      }
    },

    async toggleLicense(row, event) {
      const newValue = event.target.checked;
      const previous = row.isLicensed;

      row.isLicensed = newValue;
      row.updatingLicense = true;
      this.applyLicenseDelta(newValue ? -1 : 1);

      try {
        await this.$axios.put(
          `api/Patient/${row.id}/license`,
          { IsLicensed: newValue },
          { headers: this.authHeader() }
        );
      } catch (error) {
        row.isLicensed = previous;
        this.applyLicenseDelta(newValue ? 1 : -1);
        this.handleError(error);
      } finally {
        row.updatingLicense = false;
      }
    },

    applyLicenseDelta(delta) {
      const next = this.licenses.available + delta;
      this.licenses.available = Math.max(
        0,
        Math.min(next, this.licenses.total)
      );
    },

    async getPatients() {
      this.loadingContent = true;
      try {
        const res = await this.$axios.get("/api/patient?page=1&limit=1000", {
          headers: this.authHeader()
        });
        const list = (res.data && res.data.Data) || [];
        this.patients = list
          .filter((item) => item != null)
          .map((item) => this.mapPatient(item));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingContent = false;
        this.recalculatePagination();
      }
    },

    mapPatient(item) {
      return {
        id: item.Id,
        name: item.Name,
        registerNumber: item.RegisterNumber || "",
        gender: item.Gender || "",
        age: this.formatAge(item.BirthDate),
        cid: this.$Utilities.getPatientCid(item) || "",
        team: this.resolveTeamName(item),
        teamId: this.resolveTeamId(item),
        isLicensed: Boolean(
          this.$Utilities.getValueIgnoreCase(item, "IsLicensed") ??
            this.$Utilities.getValueIgnoreCase(item, "Licensed")
        ),
        updatingLicense: false
      };
    },

    // Resolve o nome da equipe do paciente. No Lila.API a equipe vem dentro de
    // "Doctor" (Doctor com IsTeam=true); também aceita objeto Team {Name},
    // string, campo TeamName, ou apenas TeamId (resolvido via lista de equipes).
    resolveTeamName(item) {
      const doctor = this.$Utilities.getValueIgnoreCase(item, "Doctor");
      if (doctor && typeof doctor === "object") {
        const name = this.$Utilities.getValueIgnoreCase(doctor, "Name");
        if (name) return name;
      }

      const team = this.$Utilities.getValueIgnoreCase(item, "Team");
      if (team && typeof team === "object") {
        const name = this.$Utilities.getValueIgnoreCase(team, "Name");
        if (name) return name;
      }
      if (typeof team === "string" && team) return team;

      const teamName = this.$Utilities.getValueIgnoreCase(item, "TeamName");
      if (teamName) return teamName;

      const teamId =
        this.$Utilities.getValueIgnoreCase(item, "TeamId") ??
        (team && typeof team === "object"
          ? this.$Utilities.getValueIgnoreCase(team, "Id")
          : null);

      if (teamId != null) {
        const found = this.teams.find((t) => String(t.id) === String(teamId));
        if (found) return found.name;
      }

      return "";
    },

    // Resolve o Id da equipe do paciente. No Lila.API vem em "Doctor" (Doctor
    // com IsTeam=true); também aceita objeto Team {Id} ou o escalar TeamId.
    resolveTeamId(item) {
      const doctor = this.$Utilities.getValueIgnoreCase(item, "Doctor");
      if (doctor && typeof doctor === "object") {
        const id = this.$Utilities.getValueIgnoreCase(doctor, "Id");
        if (id != null) return id;
      }
      const team = this.$Utilities.getValueIgnoreCase(item, "Team");
      if (team && typeof team === "object") {
        const id = this.$Utilities.getValueIgnoreCase(team, "Id");
        if (id != null) return id;
      }
      const teamId = this.$Utilities.getValueIgnoreCase(item, "TeamId");
      return teamId != null ? teamId : null;
    },

    parseBirthDate(birthDate) {
      const formats = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

      for (const format of formats) {
        const parsed = this.$moment(birthDate, format);
        if (parsed.isValid()) return parsed;
      }

      const iso = this.$moment(birthDate, this.$moment.ISO_8601);
      return iso.isValid() ? iso : null;
    },

    formatAge(birthDate) {
      // Sem data de nascimento (ou inválida) → célula vazia, não "—".
      if (!birthDate) return "";
      const parsed = this.parseBirthDate(birthDate);
      if (!parsed) return "";

      const now = this.$moment();
      const years = now.diff(parsed, "years");
      if (years >= 1) return `${years} ${years === 1 ? "ano" : "anos"}`;

      const months = now.diff(parsed, "months");
      return `${months} ${months === 1 ? "mês" : "meses"}`;
    },

    // ---- Edição individual da equipe (clicar no texto) ----------------
    aoClicarNaEquipe(row, event) {
      if (this.selectedTeamRowId === row.id) {
        this.selectedTeamRowId = null;
        return;
      }

      const gap = 6;
      const margin = 16;
      const popoverWidth = 320;
      const popoverHeight = 460;
      const cellRect = event.currentTarget.getBoundingClientRect();
      const left = Math.max(
        margin,
        Math.min(cellRect.left, window.innerWidth - margin - popoverWidth)
      );
      const spaceBelow = window.innerHeight - cellRect.bottom;
      const opensUp = spaceBelow < popoverHeight && cellRect.top > spaceBelow;

      this.teamPickerStyle = opensUp
        ? { left: `${left}px`, bottom: `${window.innerHeight - cellRect.top + gap}px` }
        : { left: `${left}px`, top: `${cellRect.bottom + gap}px` };
      this.selectedTeamRowId = row.id;
    },

    closeTeamPickerOnScroll(event) {
      if (this.selectedTeamRowId == null) return;
      const target = event.target;
      if (target instanceof Element && target.closest(".pmg-team-picker")) return;
      this.selectedTeamRowId = null;
    },

    // Botão "Editar": abre a Ficha do paciente (o pai faz seleção + navegação).
    // A troca de equipe continua no clique sobre o nome da equipe (coluna).
    editRow(row) {
      this.$emit("custom-edit-event", row.id);
    },

    changePatientTeam(row, teamId) {
      const id = Number(teamId);
      if (id === Number(row.teamId)) {
        this.selectedTeamRowId = null;
        return;
      }

      row.updatingTeam = true;
      this.$axios
        .put(
          `api/Patient/${row.id}/team`,
          { TeamId: id },
          { headers: this.authHeader() }
        )
        .then(() => {
          const equipe =
            this.transferTargets.find((t) => Number(t.id) === id) ||
            this.teams.find((t) => Number(t.id) === id);
          if (equipe) {
            row.team = equipe.name;
            row.teamId = id;
          }
          this.selectedTeamRowId = null;
          this.$Utilities.controlAlert(
            null,
            this.alert,
            "success",
            "Equipe atualizada com sucesso."
          );
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          row.updatingTeam = false;
        });
    },

    // Filtro por equipe: o picker (immediate) devolve o id; guardamos o nome
    // em filters.team (o matching do filtro é por nome).
    applyTeamFilter(id) {
      if (id == null) {
        this.filters.team = null;
        this.filters.teamId = null;
      } else {
        const t = this.$Utilities.findTeamOrUnit(this.filterTargets, this.teams, id);
        this.filters.team = t ? t.name : null;
        this.filters.teamId = t ? id : null;
      }
      this.filterPickerOpen = false;
      this.applyClientFilters();
    },

    handleClickOutside(event) {
      // Fecha o dropdown de filtro por equipe.
      if (this.filterPickerOpen) {
        let insideFilter = false;
        document.querySelectorAll(".pmg-filter-picker").forEach((picker) => {
          if (picker.contains(event.target)) insideFilter = true;
        });
        if (!insideFilter) this.filterPickerOpen = false;
      }

      const pickers = document.querySelectorAll(".pmg-team-picker");
      let clickedInside = false;

      pickers.forEach((picker) => {
        if (picker.contains(event.target)) clickedInside = true;
      });

      if (
        !clickedInside &&
        !event.target.classList.contains("clickable-cell")
      ) {
        this.selectedTeamRowId = null;
      }

      // Fecha o popover do bulk ao clicar fora dele (e fora do botão).
      if (this.bulkPickerOpen) {
        const bulkPicker = document.querySelector(".pmg-bulk-picker");
        const onApplyBtn = event.target.closest
          ? event.target.closest(".pmg-bulk-apply")
          : null;
        if ((!bulkPicker || !bulkPicker.contains(event.target)) && !onApplyBtn) {
          this.bulkPickerOpen = false;
        }
      }
    },

    // ---- Seleção em lote (bulk-edit) ----------------------------------
    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelect(row) {
      const index = this.selectedIds.indexOf(row.id);
      if (index === -1) this.selectedIds.push(row.id);
      else this.selectedIds.splice(index, 1);
    },

    // Marca/desmarca todos os pacientes da página visível.
    toggleSelectAllPage(event) {
      const pageIds = this.pagedRows.map((row) => row.id);
      if (event.target.checked) {
        pageIds.forEach((id) => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
        });
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => !pageIds.includes(id)
        );
      }
    },

    clearSelection() {
      this.selectedIds = [];
      this.bulkPickerOpen = false;
    },

    // Altera a equipe de todos os selecionados — PUT api/Patient/team/bulk.
    // Recebe o id da equipe escolhida na árvore (single-select).
    async applyBulkTeam(teamId) {
      const id = teamId != null ? Number(teamId) : null;
      if (!id) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "warning",
          "Selecione a equipe."
        );
        return;
      }
      if (this.selectedIds.length === 0) return;

      this.bulkSaving = true;
      try {
        const res = await this.$axios.put(
          "api/Patient/team/bulk",
          { PatientIds: this.selectedIds.slice(), TeamId: id },
          { headers: this.authHeader() }
        );

        const data = res.data || {};
        const updated =
          this.$Utilities.getValueIgnoreCase(data, "Updated") || [];
        const equipe =
          this.transferTargets.find((t) => Number(t.id) === id) ||
          this.teams.find((t) => Number(t.id) === id);

        // Reflete na tabela apenas os pacientes efetivamente alterados.
        updated.forEach((pid) => {
          const row = this.patients.find((p) => p.id === pid);
          if (row && equipe) {
            row.team = equipe.name;
            row.teamId = id;
          }
        });

        this.notifyBulkResult(data, updated.length);
        this.clearSelection();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.bulkSaving = false;
      }
    },

    // Monta o alerta de resultado considerando os que não puderam ser alterados.
    notifyBulkResult(data, updatedCount) {
      const notFound = (
        this.$Utilities.getValueIgnoreCase(data, "NotFound") || []
      ).length;
      const forbidden = (
        this.$Utilities.getValueIgnoreCase(data, "Forbidden") || []
      ).length;
      const failed = (
        this.$Utilities.getValueIgnoreCase(data, "Failed") || []
      ).length;
      const skipped = notFound + forbidden + failed;

      if (skipped === 0) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          `Equipe alterada para ${updatedCount} ${
            updatedCount === 1 ? "paciente" : "pacientes"
          }.`
        );
      } else {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "warning",
          `${updatedCount} atualizado(s); ${skipped} não puderam ser alterados.`
        );
      }
    },

    applySearch() {
      this.filters.searchActive = this.filters.search.trim() !== "";
      this.pagination.current = 1;
    },

    clearSearch() {
      this.filters.search = "";
      this.filters.searchActive = false;
      this.pagination.current = 1;
    },

    applyClientFilters() {
      this.pagination.current = 1;
    },

    // Abre o fluxo de cadastro de paciente (passo 1).
    addPatient() {
      this.$router.push({ name: "step1PatientInformation" });
    },

    changePage(page) {
      this.pagination.current = page;
    },

    recalculatePagination() {
      const total = Math.ceil(
        this.filteredRows.length / this.pagination.perPage
      );
      this.pagination.totalPages = total;
      if (this.pagination.current > total) {
        this.pagination.current = total || 1;
      }
      if (this.pagination.current < 1) {
        this.pagination.current = 1;
      }
    },

    handleError(error) {
      const data = error && error.response ? error.response.data : null;
      if (error && error.response) {
        this.$Utilities.verifyToken(error.response.status);
      }
      this.$Utilities.controlAlert(
        data,
        this.alert,
        "danger",
        "Ocorreu um erro ao carregar as informações."
      );
    }
  }
};
</script>

<style lang="scss" scoped>
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

/* Resumo de licenças (padrão do dashboard) */
.pmg-license-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  /* Valores efetivos do .patients .license-info do dashboard. */
  color: #444444;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 40px;
}

/* Cor do badge conforme a disponibilidade (igual ao dashboard) */
.pmg-license-dot {
  color: #c7c7c7;
}

.pmg-license-contact {
  /* Link padrão (azul do Bulma), como no dashboard. */
  color: #3e8ed0;
  font-weight: 700;
  text-decoration: none;
}

.pmg-license-contact:hover {
  text-decoration: underline;
}

/* Toggle "Licenciado" */
.pmg-switch {
  display: inline-block;
  height: 22px;
  width: 45px;
  position: relative;
}

.pmg-switch input {
  display: none;
}

.pmg-slider {
  background-color: #e0e0e0;
  border-radius: 34px;
  position: absolute;
  inset: 0;
  cursor: pointer;
  transition: 0.4s;
}

.pmg-slider:before {
  content: "";
  background-color: #ffffff;
  border-radius: 50%;
  height: 16px;
  width: 16px;
  bottom: 3px;
  left: 4px;
  position: absolute;
  transition: 0.4s;
}

.pmg-switch input:checked + .pmg-slider {
  background-color: $lila-primary;
}

.pmg-switch input:checked + .pmg-slider:before {
  transform: translateX(21px);
}

.pmg-switch input:disabled + .pmg-slider {
  opacity: 0.6;
  cursor: default;
}

/* Filtros */
.pmg-filters {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: wrap;
  /* Mesmo respiro do dashboard entre os filtros e a tabela (.list-header). */
  margin: 0 0 50px;
}

/* Campos com largura fixa (não esticam para preencher a linha). */
.pmg-filter {
  flex: 0 1 320px;
  min-width: 240px;
}

.pmg-filter--search {
  flex: 0 1 420px;
}

/* Botão "Adicionar" com largura fixa e larga (padrão do dashboard: 250px). */
.pmg-filter--add {
  flex: 0 0 250px;
  min-width: 0;
}

.pmg-filter-label {
  display: block;
  font-size: 14px;
  line-height: 18px;
  color: #3a3a3a;
  margin-bottom: 8px;
}

/* Divisória vertical antes do botão adicionar (empurra o "Adicionar" p/ a direita) */
.pmg-divider {
  align-self: stretch;
  width: 1px;
  background: #e4e1ee;
  margin: 0 4px 0 auto;
}

/* Botão "Adicionar Paciente" */
.pmg-add-btn {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: $lila-accent;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.pmg-add-icon {
  flex-shrink: 0;
}

.pmg-add-btn:hover {
  background: darken($lila-accent, 6%);
}

/* Barra de ação em lote (bulk-edit) */
.pmg-bulk-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 12px 16px;
  background-color: #f4f2fa;
  border: 1px solid #e2ddf0;
  border-radius: 10px;
}

.pmg-bulk-count {
  font-weight: 600;
  color: $lila-primary;
}

.pmg-bulk-action {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Popover da árvore de equipes no bulk */
.pmg-bulk-picker-wrap {
  position: relative;
}

.pmg-bulk-picker {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  z-index: 600;
}

.pmg-bulk-apply {
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  background-color: $lila-primary;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.pmg-bulk-apply:hover:not(:disabled) {
  opacity: 0.9;
}

.pmg-bulk-apply:disabled {
  opacity: 0.5;
  cursor: default;
}

.pmg-bulk-clear {
  border: none;
  background: transparent;
  color: #6b6b6b;
  cursor: pointer;
  text-decoration: underline;
  margin-left: auto;
}

/* Tabela em cards */
.pmg-table-wrapper {
  overflow-x: auto;
}

/* Tabela em cards — mesmos valores do is-lila-table do dashboard:
   cards planos (sem sombra), gap 10px, cantos 15px, células 20px 25px. */
.pmg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
}

.pmg-table thead th {
  text-align: left;
  font-size: 14px;
  color: #3a3a3a;
  font-weight: 600;
  padding: 0 25px 5px;
  border: none; /* remove a linha inferior herdada do Bulma (.content table th) */
}

.pmg-table tbody td {
  padding: 20px 25px;
  font-size: 16px;
  line-height: 20px;
  color: #3a3a3a;
  vertical-align: middle;
  background-color: #ffffff;
}

.pmg-table tbody tr.is-selected td {
  background-color: rgba(79, 63, 130, 0.06);
}

.pmg-table tbody td:first-child {
  border-top-left-radius: 15px;
  border-bottom-left-radius: 15px;
}

.pmg-table tbody td:last-child {
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
}

.pmg-name {
  color: #3a3a3a;
  font-weight: 400;
}

.pmg-check-col {
  width: 44px;
}

.pmg-check {
  display: inline-flex;
  cursor: pointer;
}

.pmg-check input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: $lila-primary;
}

/* Edição de equipe (clicar no texto → dropdown) */
.team-cell {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}

.clickable-cell {
  cursor: pointer;
  text-decoration: none !important;
}

.clickable-cell:hover {
  text-decoration: underline;
  color: $lila-accent;
}

.pmg-edit-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
}

.pmg-edit-btn svg,
.pmg-edit-btn img {
  width: 18px;
  height: 18px;
}

/* O SvgIcon tem @click próprio com stopPropagation; se o clique cair em
   qualquer descendente (div wrapper, <svg>, <path>...) ele é engolido antes
   de chegar no <button>, exigindo um segundo clique. Neutralizar TUDO dentro
   do botão pra que só o próprio <button> receba o evento. */
.pmg-edit-btn * {
  pointer-events: none;
}

/* Popover do seletor de equipe em árvore (posicionamento) */
.pmg-team-picker {
  position: fixed;
  z-index: 600;
}

/* Filtro de Equipe: botão-gatilho no estilo dos campos + popover do picker */
.pmg-filter-picker-wrap {
  position: relative;
}

.pmg-filter-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  padding: 11px 20px;
  font-size: 14px;
  color: #3a3a3a;
  cursor: pointer;
  font-family: inherit;
}

.pmg-filter-select > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pmg-filter-select .is-placeholder {
  color: #9a95a8;
}

.pmg-filter-caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid $lila-accent;
  flex: none;
}

.pmg-filter-select.has-clear {
  padding-right: 48px;
}

.pmg-filter-clear {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0 0 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: $lila-accent;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

.pmg-filter-clear:hover {
  opacity: 0.7;
}

.pmg-filter-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

.pmg-empty {
  padding: 30px 0;
  color: #9a9a9a;
}
</style>
